import React from "react";
import Link from "gatsby-link";
import styled from "styled-components";
import { H3, H4, H5, Description } from "./typography";
import AspectImage from "./atoms/AspectImage";
import { r, getThemeColor, slugify } from "../utils";
import {
  HideTablet,
  HideDesktop,
  HideMobile,
  ShowMobile
} from "../components/blocks/Responsive";

const FeatureDescription = styled.div`
  position: absolute;
  left: 5rem;
  right: 8rem;
  bottom: 3rem;
  top: 1rem;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;

  ${props => props.theme.media.tablet`
    left: 2rem;
    bottom: .5rem;
    right: 2rem;
  `};

  ${props => props.theme.media.mobile`
    left: 2rem;
    bottom: .5rem;
    right: 2rem;
  `};
`;

const PostType = Description.extend`
  display: inline-block;
  padding: 0;
  padding-right: 3rem;
`;

const PostDate = Description.extend`
  display: inline-block;
  font-weight: 100;
  font-size: .8rem;
  padding: 0;
`;

const Thumbnail = styled.div`
  display: flex;
  height: 9rem;
  padding: 1.5rem 0;
  :first-child {
    padding-top: 0;
  }
  ${props => props.theme.media.desktop`
    height: auto;
  `};
  ${props => props.theme.media.card`
    flex-direction: column;
    height: auto;
    width: auto;
  `};
`;

const ThumbnailBody = styled.div`
  flex: 3;
  background-color: ${getThemeColor("white")};
  padding: 1rem 2rem;
  overflow: hidden;
`;

const ThumbnailImage = styled.div`
  display: flex;
  flex: 1;
`;

const Title = styled.div`
  padding-bottom: .25rem;
  overflow: hidden;
  text-overflow: ellipsis;

  ${props => props.theme.media.desktop`
`};
`;

const InlineH5 = H5.extend`
  display: inline-block;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
`;

const PostTitle = ({ color, post }) =>
  <Title>
    <InlineH5 color={color}>
      <b style={{ paddingRight: ".15rem", fontWeight: 600 }}>
        {post.title}
      </b>
      {post.subtitle}
    </InlineH5>
  </Title>;

const PostPreview = Description.extend`
  font-size: .9rem;
  line-height: 1.3rem;

  /* styles for '...' */
  /* hide text if it more than N lines  */
  overflow: hidden;
  /* for set '...' in absolute position */
  position: relative;
  /* use this value to count block height */
  /* max-height = line-height (1.2) * lines max number (3) */
  max-height: 3.8em;
  /* fix problem when last visible word doesn't adjoin right side  */
  text-align: justify;
  /* place for '...' */
  margin-right: -1em;
  padding-right: 1em;

  /* create the ... */
  &:before {
    /* points in the end */
    content: '...';
    /* absolute position */
    position: absolute;
    /* set position to right bottom corner of block */
    right: 0;
    bottom: 0;
  }
  /* hide ... if we have text, which is less than or equal to max lines */
  &:after {
    /* points in the end */
    content: '';
    /* absolute position */
    position: absolute;
    /* set position to right bottom corner of text */
    right: 0;
    /* set width and height */
    width: 1em;
    height: 1em;
    margin-top: 0.2em;
    /* bg color = bg color under block */
    background: white;
  }
`;

const ClearLink = styled(Link)`
  text-decoration: none;
  color: inherit;
`;

export const PostThumbnail = ({ post, index }) =>
  <ClearLink to={`/news/${post.id}#${slugify(post.title)}`}>
    <Thumbnail>
      <ThumbnailImage>
        <HideDesktop>
          <AspectImage
            direction="vertical"
            src={post.headerImage.responsiveResolution.src}
          />
        </HideDesktop>
        <HideTablet>
          <AspectImage
            direction="horizontal"
            src={post.headerImage.responsiveResolution.src}
          />
        </HideTablet>
      </ThumbnailImage>
      <ThumbnailBody>
        <PostDate serif>
          {post.publishDate.split(" ").slice(0, 3).join(" ")}
        </PostDate>
        <PostTitle color="dark" post={post} />
        <PostPreview>
          {post.body.body
            .replace(/\n/, "  ")
            .replace(/[^0-9,.\-\ A-Za-z]/g, "")}
        </PostPreview>
      </ThumbnailBody>
    </Thumbnail>
  </ClearLink>;

const Meta = styled.div`
  display: flex;
  align-items: center;
  paddingBottom: .5rem;
  ${props => props.theme.media.mobile`
    display: none;
  `};
`;

const FeatureTitle = H3.extend`${r("font-size", "1.875rem", "1rem")};`;

const PostMeta = ({ type, date }) =>
  <Meta>
    <PostType color="white">
      Featured {type}
    </PostType>
    <PostDate serif color="white">
      {date.split(" ").slice(0, 3).join(" ")}
    </PostDate>
  </Meta>;

export const PostLink = ({
  post: { id, title, subtitle, type, publishDate, headerImage },
  children
}) =>
  <Link
    to={`/news/${id}#${slugify(title)}`}
    style={{
      textDecoration: "none",
      color: "inherit"
    }}
  >
    {children}
  </Link>;

export const PostFeature = ({
  post: { id, title, subtitle, type, publishDate, headerImage }
}) =>
  <ClearLink to={`/news/${id}#${slugify(title)}`}>
    <AspectImage src={headerImage && headerImage.responsiveResolution.src}>
      <HideMobile>
        <FeatureDescription>
          <PostMeta type={type} date={publishDate} />
          <FeatureTitle color="white">
            {title + " " + subtitle}
          </FeatureTitle>
        </FeatureDescription>
      </HideMobile>
    </AspectImage>
    <ShowMobile>
      <H4 style={{ padding: "2rem .5rem 0 .5rem" }}>
        {title + " " + subtitle}
      </H4>
    </ShowMobile>
  </ClearLink>;

export const PostBody = styled.div`
  p,
  li {
    max-width: 680px;
    padding-bottom: 1rem;
    font-size: 1.2rem;
    line-height: 1.6;
  }
  ul {
    padding-left: 1rem;
  }
  a {
    text-decoration: none;
  }
  b,
  strong {
    font-weight: 500;
  }
  h1,
  h2,
  h3,
  h4,
  h5,
  h6 {
    font-size: 1.4rem;
    line-height: 1.2;
    padding-bottom: .8rem;
    font-family: Palantino;
    font-weight: 500;
  }
  img {
    padding: 2rem 0;
    width: 100%;
    position: relative;
    right: -4rem;
    left: 0rem;
  }
  blockquote {
    margin: 3rem .5rem;
  }
  blockquote p {
    font-size: 1.8rem;
    line-height: 1.4;
    padding: 2rem 0;
  }
  blockquote:before,
  blockquote:after {
    content: ' ';
    width: 2rem;
    height: 4px;
    position: absolute;
    background: ${({ theme }) => theme.colors.teal};
  }

  padding-left: 5rem;
  padding-right: 6rem;
  @media (max-width: 720px) {
    padding-left: .5rem;
    padding-right: .5rem;
  }
`;



// WEBPACK FOOTER //
// ./src/components/Post.js